/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import IObservable from '../../Observable/IObservable';
import Observable from '../../Observable/Observable';
import TypeStrings from '../../TypeStrings';
import ReferencedComponent from '../ComponentReference/ReferencedComponent';
import ObservableEvent from './ObservableEvent';

/** Generic observable pattern that dispatches events when the observed value changes. */
@cc._decorator.ccclass
export default abstract class ObservableComponentBase<T> extends ReferencedComponent implements IObservable<T>
{
    /** The event name used when the observed value has changed. */
    public static readonly ValueChangedEventName: string = "Observable.ValueChanged";

    /** The observed value. */
    protected abstract value?: T;
    /** Accessor for the observed value. */
    public get Value(): T { return this.GetValue(); }
    /** Convenience mutator for the observed value, routes through SetValue. */
    public set Value(value) { this.SetValue(value); }
    public HasValue(): boolean { return this.value !== null && this.value !== undefined; }

    /** The previous value. */
    protected oldValue: T = null;
    /** Accessor for the previous value. */
    public get OldValue(): T { return this.oldValue; }

    /** A collection of valid values, all values valid if empty. */
    protected validValues: T[] = [];
    /** Convenience accessor for if the validValues collection is not empty. */

    protected _isReadOnlyObservable: Observable<boolean> = new Observable<boolean>(false);
    public get IsReadOnlyObservable(): Observable<boolean> { return this._isReadOnlyObservable; }

    public Lock(): void { this._isReadOnlyObservable.Value = true; }
    public UnLock(): void { this._isReadOnlyObservable.Value = false; }

    public get IsValueRestricted(): boolean { return this.validValues.length > 0; }
    /** The index of the current value in the validValues collection. */
    public get ValueIndex(): number
    {
        if (this.validValues.length < 1) { return 0; }
        return this.validValues.indexOf(this.value);
    }
    /**
     * A percentage value, [0.0, 1.0] of where in the validValues collection the current value lies.
     * 0 if no validValues set.
     */
    public get ValueProgress(): number
    {
        if (this.validValues.length < 1) { return 0; }

        return this.ValueIndex / (this.validValues.length - 1);
    }
    /**
     * The percentage value difference between each index in the validValues collection.
     */
    public get ValueStep(): number
    {
        if (this.validValues.length <= 1) { return 1; }
        return 1.0 / (this.validValues.length - 1);
    }

    /** The maximum allowed value. */
    public abstract get MaxValue(): T;
    /** The minimum allowed value. */
    public abstract get MinValue(): T;

    /** @hidden */
    protected onLoad(): void { super.onLoad(); }
    /** @hidden */
    protected start(): void { super.start(); }

    /** @hidden */
    protected firstUpdate(dt: number): void { super.firstUpdate(dt); }

    /** @hidden */
    protected update(dt: number): void { /* Placeholder */ }
    /** @hidden */
    protected onDestroy(): void { /* Placeholder */ }

    /**
     * Sets the new value and emits a value changed event,
     * so long as the new value and old value are different.
     */
    public SetValue(value: T): ObservableComponentBase<T>
    {
        if (this._isReadOnlyObservable.Value || this.value === value) { return this; }

        this.oldValue = this.value;

        this.value = value;

        this.broadcastUpdate();

        return this;
    }

    /** Force a dispatch of the onChanged event. */
    public ForceUpdate = (): void =>
    {
        this.broadcastUpdate();
    }

    /** Returns the current value. */
    public GetValue(): T
    {
        return this.value;
    }

    /**
     * Register a function to be executed each time the value changes.
     * @param fnOnChange The function to be executed.
     */
    public OnChanged(fnOnChange: (event?: ObservableEvent<T>) => void): void
    {
        this.node.on(ObservableComponentBase.ValueChangedEventName, (event: ObservableEvent<T>) => fnOnChange(event));
    }

    /**
     * Register a function to be executed each time the value changes.
     * @param fnOnChange The function to be executed.
     */
    public OnChangedOnce(fnOnChange: (event?: ObservableEvent<T>) => void): void
    {
        this.node.once(ObservableComponentBase.ValueChangedEventName, (event: ObservableEvent<T>) => fnOnChange(event));
    }

    /**
     * Unregister a function that was to be called each time the value changed.
     * @param fnOnChange The function to be unregistered.
     */
    public OffChanged(fnOnChange: (event?: ObservableEvent<T>) => void): void
    {
        // Make sure the node still exists.
        if (this.node)
        {
            this.node.off(ObservableComponentBase.ValueChangedEventName,
                (event: ObservableEvent<T>) => fnOnChange(event));
        }
    }

    /**
     * Sets the value to the next lowest value down the validValues list.
     * @param toProgress optional set to closest progress in validValues list.
     */
    public SetNextLowerValidValue(toProgress: number = null): void
    {
        if (!this.IsValueRestricted && typeof(this.value) === TypeStrings.number)
        {
            let newValue: any = this.value;
            (newValue as number)--;

            if (newValue < this.MinValue) { return; }

            this.SetValue(newValue);

            return;
        }

        if (toProgress !== null)
        {
            this.setToValidProgress(toProgress, -1);
            return;
        }

        let index: number = this.ValueIndex;
        index = (--index > 0) ? index : 0;
        this.setToValidIndex(index);
    }

    /**
     * Sets the value to the next highest value up the validValues list.
     * @param toProgress optional set to closest progress in validValues list.
     */
    public SetNextHigherValidValue(toProgress: number = null): void
    {
        if (!this.IsValueRestricted && typeof(this.value) === TypeStrings.number)
        {
            let newValue: any = this.value;
            (newValue as number)++;

            if (newValue > this.MaxValue)
            {
                return;
            }

            this.SetValue(newValue);
            return;
        }

        if (toProgress !== null)
        {
            this.setToValidProgress(toProgress, 1);
            return;
        }

        let index: number = this.ValueIndex;
        index = (++index < this.validValues.length - 1) ? index : this.validValues.length - 1;
        this.setToValidIndex(index);
    }

    /**
     * Sets value to validValue index closest to a certain percentage.
     * @param progress percentage to set to.
     */
    private setToValidProgress(progress: number, direction: number): void
    {
        let indexProgress: number = (this.validValues.length - 1) * progress;
        let index: number = direction > 0
            ? Math.ceil(indexProgress)
            : Math.floor(indexProgress);

        this.setToValidIndex(index);
    }

    /**
     * Set to specific index in validValues array.
     * @param index index to set to.
     */
    private setToValidIndex(index: number): void
    {
        this.SetValue(this.validValues[index % this.validValues.length]);
    }

    /**
     * Adds a new valid value to the validValues collection, if it does not already exist.
     * @param value The valid value to add.
     */
    public AddValidValue(value: T): void
    {
        if (this.validValues.some(v => v === value)) { return; }
        this.validValues.push(value);
        this.broadcastUpdate(false);
    }

    /**
     * Empties the validValues collection.
     */
    public ClearValidValues(): void
    {
        this.validValues = [];
    }

    /**
     * Checks against the validValues collection to see if the current value is valid.
     * @returns If the current value is valid.
     */
    public IsValid(): boolean
    {
        if (!this.isInValidValues()) { return false; }

        return true;
    }

    /**
     * Helper for emitting the value changed event.
     * @hidden
     */
    protected broadcastUpdate(rootValueChanged: boolean = true): void
    {
        const event: ObservableEvent<T>
            = new ObservableEvent(
                ObservableComponentBase.ValueChangedEventName,
                true,
                {
                    Detail: this.value,
                    RootvalueChanged: rootValueChanged
                }
            );
        this.node.dispatchEvent(event);
    }

    /**
     * Helper for determining if the current value is valid.
     * @returns If the value is valid.
     * @hidden
     */
    private isInValidValues(): boolean
    {
        if (this.validValues.length === 0) { return true; }
        return this.validValues.some((x) => x === this.value);
    }
}
